/**
 * SF-4651: the one map on the Demos pages. It shows the search circle and lets the person click a
 * point to use as its centre. SF-4652: the review page also pins each demo location, named in the
 * pin's tooltip.
 *
 * Uses @react-google-maps/api, the library sf-react-app already uses for its location maps, kept
 * behind this component so the rest of the wizard never touches Google directly (and the tests
 * swap this file for a stub). The browser key comes from VITE_GOOGLE_MAPS_API_KEY; without one the
 * map says so and the town / postcode search still works.
 */
import { Circle, GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import { Alert, Skeleton } from 'antd';
import { GOOGLE_MAPS_API_KEY } from '../../../config/env';

const METRES_PER_MILE = 1609.344;
// Great Britain, for when no point has been chosen yet.
const DEFAULT_CENTRE = { lat: 54.5, lng: -2.5 };
const MAP_STYLE = { width: '100%', height: 360, borderRadius: 8 };

export interface AreaMapProps {
  centre: { lat: number; lng: number } | null;
  radiusMiles: number | null;
  onPick: (point: { lat: number; lng: number }) => void;
  /** Places to pin, each with the name its tooltip shows (SF-4652). */
  pins?: MapPin[];
}

export interface MapPin {
  key: string;
  position: { lat: number; lng: number };
  title: string;
}

/** Zoom that keeps the whole circle in a ~360 px tall map. */
const zoomFor = (radiusMiles: number | null) =>
  radiusMiles ? Math.max(6, Math.min(14, Math.round(13.5 - Math.log2(radiusMiles)))) : 5;

const LoadedMap: React.FC<AreaMapProps & { apiKey: string }> = ({
  apiKey,
  centre,
  radiusMiles,
  onPick,
  pins = [],
}) => {
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'sf-admin-google-map',
    googleMapsApiKey: apiKey,
  });
  if (loadError) return <Alert type="warning" showIcon message="The map could not load" />;
  if (!isLoaded) return <Skeleton.Node active style={{ width: '100%', height: 360 }} />;
  return (
    <GoogleMap
      mapContainerStyle={MAP_STYLE}
      center={centre ?? DEFAULT_CENTRE}
      zoom={centre ? zoomFor(radiusMiles) : 5}
      options={{ streetViewControl: false, mapTypeControl: false, clickableIcons: false }}
      onClick={(e) => {
        const lat = e.latLng?.lat();
        const lng = e.latLng?.lng();
        if (lat !== undefined && lng !== undefined) onPick({ lat, lng });
      }}
    >
      {centre && <Marker position={centre} />}
      {pins.map((pin) => (
        <Marker key={pin.key} position={pin.position} title={pin.title} />
      ))}
      {centre && radiusMiles ? (
        <Circle
          center={centre}
          radius={radiusMiles * METRES_PER_MILE}
          options={{
            fillColor: '#1677ff',
            fillOpacity: 0.12,
            strokeColor: '#1677ff',
            clickable: false,
          }}
        />
      ) : null}
    </GoogleMap>
  );
};

export const AreaMap: React.FC<AreaMapProps> = (props) => {
  if (!GOOGLE_MAPS_API_KEY) {
    return (
      <Alert
        type="info"
        showIcon
        message="The map is not set up for this admin app"
        description="Search for a town or postcode instead. (No Google Maps key is configured.)"
      />
    );
  }
  return <LoadedMap {...props} apiKey={GOOGLE_MAPS_API_KEY} />;
};
